import React, { createContext, useCallback, useContext, useMemo, useState } from "react";
import { en } from "./en";
import type { Strings } from "./en";
import { ko } from "./ko";

export type Language = "en" | "ko" | "fr";

const STORAGE_KEY = "pronocup_language";

// NOTE: "fr" maps to English for the GLOBAL strings on purpose — French is only
// meant to translate the Stadium Trial mode (which reads `language` locally). So
// everywhere else the app stays in English when French is selected.
const STRINGS: Record<Language, Strings> = { en, ko, fr: en };

function detectLanguage(): Language {
  try {
    const saved = localStorage.getItem(STORAGE_KEY) as Language | null;
    if (saved === "en" || saved === "ko" || saved === "fr") return saved;
  } catch {}
  return navigator.language.startsWith("ko") ? "ko" : "en";
}

function resolve(obj: any, path: string): any {
  const keys = path.split(".");
  let cur = obj;
  for (const k of keys) {
    if (cur == null) return undefined;
    cur = cur[k];
  }
  return cur;
}

type TFn = (path: string, ...args: any[]) => string;

interface I18nContextValue {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: TFn;
}

const I18nContext = createContext<I18nContextValue | null>(null);

export function I18nProvider({ children }: { children: React.ReactNode }) {
  const [language, setLanguageState] = useState<Language>(detectLanguage);

  const setLanguage = useCallback((lang: Language) => {
    try { localStorage.setItem(STORAGE_KEY, lang); } catch {}
    setLanguageState(lang);
  }, []);

  const t = useCallback<TFn>(
    (path, ...args) => {
      const strings = STRINGS[language];
      let val = resolve(strings, path);
      if (val === undefined) val = resolve(en, path);
      if (typeof val === "function") return val(...args);
      if (typeof val === "string") return val;
      return path;
    },
    [language]
  );

  const value = useMemo(() => ({ language, setLanguage, t }), [language, setLanguage, t]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nContextValue {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error("useI18n must be used inside I18nProvider");
  return ctx;
}
